import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Notification, NotificationType } from '../types/notification';
import notificationService from '../services/notificationService';
import NotificationSnackbar from '../components/NotificationSnackbar';

interface NotificationContextData {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  createNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  deleteAllNotifications: () => void;
  fetchNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextData>({} as NotificationContextData);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedNotifications = notificationService.getNotifications();
      setNotifications(fetchedNotifications);
      setUnreadCount(notificationService.getUnreadCount());
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const createNotification = useCallback((notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => {
    const newNotification = notificationService.createNotification(notification);
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
    setCurrentNotification(newNotification);
  }, []);

  const markAsRead = useCallback((id: string) => {
    notificationService.markAsRead(id);
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    notificationService.markAllAsRead();
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
    setUnreadCount(0);
  }, []);

  const deleteNotification = useCallback((id: string) => {
    notificationService.deleteNotification(id);
    setNotifications(prev => prev.filter(notification => notification.id !== id));
    setUnreadCount(prev => {
      const deletedNotification = notifications.find(n => n.id === id);
      return deletedNotification && !deletedNotification.read ? Math.max(0, prev - 1) : prev;
    });
  }, [notifications]);

  const deleteAllNotifications = useCallback(() => {
    notificationService.deleteAllNotifications();
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const handleCloseSnackbar = () => {
    setCurrentNotification(null);
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        createNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        deleteAllNotifications,
        fetchNotifications,
      }}
    >
      {children}
      <NotificationSnackbar
        notification={currentNotification}
        onClose={handleCloseSnackbar}
      />
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications deve ser usado dentro de um NotificationProvider');
  }
  return context;
};

export default NotificationContext; 